/*
    This file contains some functions that can be used for some common tasks throughout the application
*/
import { deleteItems, updateStateData } from "../Store/actions";
import store from "../Store/store";
import * as xlsx from "xlsx";
import platformConfig from "../platformConfig";
import moment from "moment-timezone";
import { deleteCookie, sendApiRequest } from "./requestConfig";
import { checkUserDetailsFromCookie } from "./userManagement";
import { ArrowDropDown, ArrowDropUp, Filter, FilterAlt, Store } from "@mui/icons-material";
import { HtmlTooltip } from "../components/commonComponents/StyledComponents/styled";
import { IconButton } from "@mui/material";
// import moment from 'moment';

export const weekDayNames = {
  0: "Sunday",
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday",
};

export const WEEKDAY_KEYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
export const WEEKDAY_LOWER_TO_INT = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };
export const WEEKDAY_INT_TO_LOWER = { 0: "sunday", 1: "monday", 2: "tuesday", 3: "wednesday", 4: "thursday", 5: "friday", 6: "saturday" };
export const WEEKDAY_DISPLAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

// Renders an ISO date (YYYY-MM-DD) as the app-wide display format DD/MM/YYYY.
// Non-date values (e.g. "—", "Indefinitely") are returned untouched.
export const formatDateDMY = (value) => {
  if (!value) return value;
  const parsed = moment(value, "YYYY-MM-DD", true);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : value;
};

// Special hours are a single date: future = scheduled, today = active, past = expired.
export const getSpecialHoursStatus = (entry, today) => {
  if (!entry?.valid_on) return "scheduled";
  if (entry.valid_on > today) return "scheduled";
  if (entry.valid_on < today) return "expired";
  return "active";
};

// Quiet "once" uses the same date rules as special hours. Weekly: no start or
// start in the future = scheduled; bounded end already past = expired; otherwise active.
export const getQuietHoursStatus = (entry, today) => {
  if (entry?.type === "once") return getSpecialHoursStatus(entry, today);
  if (!entry?.validity_start) return "scheduled";
  if (entry.validity_start > today) return "scheduled";
  if (!entry.no_end && entry.validity_end && entry.validity_end < today) return "expired";
  return "active";
};

export const allWeekDayOptions = [
  // { label: "All", value: "all" },
  { label: "Monday", value: "Monday" },
  { label: "Tuesday", value: "Tuesday" },
  { label: "Wednesday", value: "Wednesday" },
  { label: "Thursday", value: "Thursday" },
  { label: "Friday", value: "Friday" },
  { label: "Saturday", value: "Saturday" },
  { label: "Sunday", value: "Sunday" },
];

export const weekDayNum = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

// to calculate the week number from the given date.
function getISOWeekNumber(date) {
  const startOfYear = new Date(date.getFullYear(), 0, 1);
  const weekNumber = Math.ceil(((date - startOfYear) / 86400000 + startOfYear.getDay() + 1) / 7);
  return weekNumber;
}

// to return an array of week numbers for a given range of dates.
export const fetchWeekNumbers = async (startDateStr, endDateStr) => {
  const startDate = moment(startDateStr).tz(platformConfig.timezone);
  const endDate = moment(endDateStr).tz(platformConfig.timezone);

  const weekNumbers = [];
  let weekNumbersArr = [];
  let currentDate = startDate.clone();

  while (currentDate.isSameOrBefore(endDate)) {
    const weekNumber = currentDate.isoWeek();
    if (!weekNumbersArr.includes(weekNumber)) {
      weekNumbers.push({ value: weekNumber, label: weekNumber.toString() });
      weekNumbersArr.push(weekNumber);
    }

    currentDate.add(1, "days");
  }

  store.dispatch(
    updateStateData({
      campaign_weeks: weekNumbers,
    })
  );
  return weekNumbers;
};

// validate if the provided field is empty
export const validateEmpty = (fieldValue, isArray = false) => {
  let error = false;
  if (fieldValue === "") {
    error = true;
  } else {
    if (isArray === true) {
      if (fieldValue !== "" && fieldValue.length === 0) {
        error = true;
      }
    }
  }

  return {
    error: error,
  };
};

export const validateInputLength = (fieldValue, charLength) => {
  let error = false;
  if (fieldValue !== "") {
    if (fieldValue.length > charLength) {
      error = true;
    }
  }

  return {
    error: error,
  };
};

//only letters validation
export const validateAlphabets = (value, lengthCheck = false, charLength = 0, allowSlash = false) => {
  let error = false;
  let transformedValue = value.toString();
  // Modify the regex pattern based on allowSlash parameter
  let letters = allowSlash ? /^[A-Za-z /]+$/ : /^[A-Za-z ]+$/;

  //value must not be empty
  if (transformedValue.trim() === "") {
    error = true;
  } else {
    if (!transformedValue.match(letters)) {
      error = true;
    }
  }

  if (error === false && lengthCheck === true) {
    if (transformedValue.length > charLength) {
      error = true;
    }
  }

  return {
    error: error,
  };
};

// only letters and numbers validation plus lengtch check
export const validateAlphaNumericAndLength = (value, lengthCheck = false, charLength = 0, allowSlash = false) => {
  let error = false;
  let transformedValue = value.toString();
  // Modify the regex pattern based on allowSlash parameter
  let letters = allowSlash ? /^[a-zA-Z0-9/]+$/ : /^[0-9a-zA-Z\s]+$/;

  //value must not be empty
  if (transformedValue.trim() === "") {
    error = true;
  } else {
    if (!transformedValue.match(letters)) {
      error = true;
    }
  }

  if (error === false && lengthCheck === true) {
    if (transformedValue.length > charLength) {
      error = true;
    }
  }

  return {
    error: error,
  };
};

//only number validation
export const validateNumbers = (value, floatValues = false) => {
  let transformedValue = value.toString();
  let error = false;
  let validNumbers = floatValues === false ? /^[0-9]+$/ : /^[0-9]+(\.[0-9]+)?$/;
  //value must not be empty
  if (transformedValue.trim() === "") {
    error = true;
  } else {
    if (!transformedValue.match(validNumbers)) {
      error = true;
    }
  }
  return {
    error: error,
  };
};

export const validateNegativeDecimalNumbers = (value) => {
  let transformedValue = value.toString();
  let error = false;
  let validNumbers = /^-?[0-9]+(\.[0-9]+)?$/;
  //value must not be empty
  if (transformedValue.trim() === "") {
    error = true;
  } else {
    if (!transformedValue.match(validNumbers)) {
      error = true;
    }
  }
  return {
    error: error,
  };
};

export const validateAlphanumeric = (value, allowDash = false) => {
  let error = false;
  // Regular expression to match alphanumeric characters
  var alphanumericRegex = allowDash ? /^[a-zA-Z0-9-]+$/ : /^[0-9a-zA-Z\s]+$/;

  // Value must not be empty
  if (value.trim() === "") {
    error = true;
  } else {
    // Check if the value contains only alphanumeric characters
    if (!value.match(alphanumericRegex)) {
      error = true;
    }
  }

  return {
    error: error,
  };
};

// Function to check if third-party cookies are enabled
export const areThirdPartyCookiesEnabled = () => {
  // Replace 'https://example.com' with the target third-party domain
  const targetDomain = "https://www.airbnb.co.uk/";

  // Create an iframe with the target domain
  const iframe = document.createElement("iframe");
  iframe.src = targetDomain;
  iframe.style.display = "none";

  // Add the iframe to the document
  document.body.appendChild(iframe);

  // Wait for a brief moment and then check if document.cookie is accessible
  return checkCookieExistance(iframe);
};

function checkCookieExistance(iframe) {
  try {
    // Attempt to read a cookie from the iframe's document
    const cookieValue = iframe.contentWindow.document.cookie;
    // If we were able to read the cookie, third-party cookies are enabled
    if (cookieValue) {
      return true;
    } else {
      return false;
    }
  } catch (error) {
    // If reading the cookie triggered a security error, third-party cookies are likely disabled
    return false;
  } finally {
    // Remove the iframe from the document
    document.body.removeChild(iframe);
  }
}

export const downloadExcelFile = (data, filename) => {
  // Create a new workbook and add a worksheet
  const workbook = xlsx.utils.book_new();
  const worksheet = xlsx.utils.aoa_to_sheet(data);

  // Add the worksheet to the workbook
  xlsx.utils.book_append_sheet(workbook, worksheet, "Sheet1");

  // Save the workbook to an Excel file
  xlsx.writeFile(workbook, filename);
};

export const formatToStandardDate = (dateToConvert) => {
  const momentObject = moment.tz(dateToConvert, platformConfig.timezone).startOf("day");

  const year = momentObject.year();
  const month = momentObject.month();
  const day = momentObject.date();
  const hours = momentObject.hours();
  const minutes = momentObject.minutes();
  const seconds = momentObject.seconds();

  const dateInSpecifiedTimezone = new Date(year, month, day, hours, minutes, seconds);

  return dateInSpecifiedTimezone;
};

export const formatToStandardDateForRangeFilter = (dateToConvert) => {
  const momentObject = moment.tz(dateToConvert, platformConfig.timezone).startOf("day");

  const year = momentObject.year();
  const month = momentObject.month();
  const day = momentObject.date();
  const hours = momentObject.hours();
  const minutes = momentObject.minutes();
  const seconds = momentObject.seconds();

  const dateInSpecifiedTimezone = new Date(year, month, day, 1, minutes, seconds);

  return dateInSpecifiedTimezone;
};

export const formatDate = (date) => {
  const formattedDate = moment.tz(date, platformConfig.timezone).format("DD/MM/YYYY");
  return formattedDate;
};

export const formatDateWithoutTimeZoneConversion = (date) => {
  const formattedDate = moment(date).format("DD/MM/YYYY");
  return formattedDate;
};

export const reformatDateToUserFormat = (date, format) => {
  const formattedDate = moment.tz(date, platformConfig.timezone).format(format);
  return formattedDate;
};

export const dbFormatDate = (date) => {
  let newDate = date;
  if (typeof date === "object") {
    // Get the day, month, and year components of the date
    const day = date.getDate().toString().padStart(2, "0"); // Ensure two digits with leading zero if needed
    const month = (date.getMonth() + 1).toString().padStart(2, "0"); // Months are zero-indexed, so add 1
    const year = date.getFullYear();
    // Format the date as "DD/MM/YYYY"
    newDate = `${year}-${month}-${day}`;
  }
  const formattedDate = moment.tz(newDate, platformConfig.timezone).format("YYYY-MM-DD");
  return formattedDate;
};

export const dateObjForToday = (dateObj) => {
  const day = dateObj.getDate().toString().padStart(2, "0"); // Ensure two digits with leading zero if needed
  const month = (dateObj.getMonth() + 1).toString().padStart(2, "0"); // Months are zero-indexed, so add 1
  const year = dateObj.getFullYear();
  // Format the date as "DD/MM/YYYY"
  return `${year}-${month}-${day}`;
};

export const checkStatus = (startDate, endDate) => {
  const todayDateObj = dateObjForToday(new Date());
  const today = moment.tz(todayDateObj, platformConfig.timezone).startOf("day");

  const campaignStartDate = moment.tz(startDate, platformConfig.timezone).startOf("day");
  const campaignEndDate = moment.tz(endDate, platformConfig.timezone).startOf("day");

  if (today.isBefore(campaignStartDate)) {
    return "Scheduled";
  } else if (today.isSameOrAfter(campaignStartDate) && today.isSameOrBefore(campaignEndDate)) {
    return "Active";
  } else {
    return "Completed";
  }
};

export const checkDaysInformation = (startDate, endDate) => {
  const todayDateObj = dateObjForToday(new Date());
  const today = moment.tz(todayDateObj, platformConfig.timezone).startOf("day");
  // const campaignStartDate = moment(startDate, "YYYY-MM-DD").tz(platformConfig.timezone).startOf("day");
  // const campaignEndDate = moment(endDate, "YYYY-MM-DD").tz(platformConfig.timezone).startOf("day");

  const campaignStartDate = moment.tz(startDate, platformConfig.timezone).startOf("day");
  const campaignEndDate = moment.tz(endDate, platformConfig.timezone).startOf("day");

  if (today.isBefore(campaignStartDate)) {
    // scheduled
    const daysUntilStart = campaignStartDate.diff(today, "days");
    return daysUntilStart === 1 ? "Starting in " + daysUntilStart + " day" : "Starting in " + daysUntilStart + " days";
  } else if (today.isSameOrAfter(campaignStartDate) && today.isSameOrBefore(campaignEndDate)) {
    // active
    const daysLeft = campaignEndDate.diff(today, "days");
    return daysLeft === 0
      ? "Ending today"
      : daysLeft === 1
        ? "Ending in " + daysLeft + " day"
        : "Ending in " + daysLeft + " days";
  } else {
    // completed
    const daysAgo = today.diff(campaignEndDate, "days");
    // return "Ended " + daysAgo + " days ago";
    return daysAgo === 1 ? "Ended 1 day ago" : "Ended " + daysAgo + " days ago";
  }
};

export const swapKeyValues = (obj) => {
  const swappedObj = {};

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      swappedObj[obj[key]] = key;
    }
  }

  return swappedObj;
};

export const checkProgress = (startDate, endDate) => {
  const today = moment().startOf("day");
  // const campaignStartDate = moment(startDate, "YYYY-MM-DD").tz(platformConfig.timezone).startOf("day");
  // const campaignEndDate = moment(endDate, "YYYY-MM-DD").tz(platformConfig.timezone).startOf("day");

  const campaignStartDate = moment.tz(startDate, platformConfig.timezone).startOf("day");
  const campaignEndDate = moment.tz(endDate, platformConfig.timezone).startOf("day");

  if (today.isBefore(campaignStartDate)) {
    return 0;
  } else if (today.isSameOrAfter(campaignStartDate) && today.isSameOrBefore(campaignEndDate)) {
    const daysLeft = campaignEndDate.diff(today, "days");
    const totalDays = campaignEndDate.diff(campaignStartDate, "days");
    const completionProgress = Math.round(((totalDays - daysLeft) / totalDays) * 100);
    return completionProgress;
  } else {
    return 100;
  }
};

// to return a random number
export const generateRandomNumber = (digits) => {
  const min = 10 * (digits - 1);
  const max = 10 * digits - 1;

  return Math.floor(Math.random() * (max - min + 1)) + min;
};

let _localScheduleCounter = 0;
export const nextLocalScheduleId = () => "local_" + (++_localScheduleCounter);

// to replace _ with space and capitalize the first character of the string
export const capitalizeAndReplaceUnderScore = (inputString, capitalize = true) => {
  let result = inputString
    .replace(/_/g, " ") // Replace underscores with spaces
    .toLowerCase(); // Convert the entire string to lowercase

  if (capitalize === true) {
    // Capitalize the first character of each word
    result = result.replace(/(?:^|\s)\S/g, (match) => match.toUpperCase());
  }
  return result;
};

export const allNumbersBetweenRange = (start, end) => {
  const result = [];

  for (let i = start; i <= end; i++) {
    result.push(i);
  }

  return result;
};

export const allNumbersBetweenRangeHoursWrapAround = (start, end) => {
  const result = [];

  if (start <= end) {
    for (let i = start; i < end; i++) {
      result.push(i);
    }
  } else {
    for (let i = start; i <= 23; i++) {
      result.push(i);
    }
    for (let i = 0; i < end; i++) {
      result.push(i);
    }
  }

  return result;
};

// validate email format
export const validateEmailFormat = (email) => {
  // Regular expression for a basic email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  return emailRegex.test(email);
};

export const validateEmailFormatWithReason = (email) => {
  // Check for empty input
  if (!email) {
    return {
      isValid: false,
      reason: "Email is required.",
    };
  }

  // Check for spaces
  if (/\s/.test(email)) {
    return {
      isValid: false,
      reason: "E-mail address cannot contain spaces.",
    };
  }

  // Basic email format
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!emailRegex.test(email)) {
    return {
      isValid: false,
      reason: "Please use a valid e-mail format like name@example.com",
    };
  }

  return {
    isValid: true,
    reason: null,
  };
};

// to load more results (Paginated) for the table listings
export const loadMoreData = (reduxKeyword, usedFor) => {
  const currentStateData = store.getState().userSelections;
  store.dispatch(
    updateStateData({
      [reduxKeyword]: currentStateData[reduxKeyword] + platformConfig.initial_load_count,
    })
  );
  // Smooth scroll to a specific element or position on the page
  const yOffset = 100; // Adjust this value as needed
  const elementToScrollTo = document.getElementById("infoText");

  if (typeof usedFor !== "undefined" && usedFor === "modal") {
    const modalContent = document.querySelector(".MuiDialogContent-root");

    if (modalContent) {
      modalContent.scrollTo({
        top: document.getElementById("infoModalText").offsetTop - yOffset,
        behavior: "smooth",
      });
    }
  } else {
    window.scrollTo({
      top: elementToScrollTo.offsetTop - yOffset,
      behavior: "smooth",
    });
  }
};

export const isValidFileName = (fileName) => {
  // Split the fileName into name and extension based on the last dot
  const lastDotIndex = fileName.lastIndexOf(".");
  if (lastDotIndex === -1 || lastDotIndex === 0 || lastDotIndex === fileName.length - 1) {
    // There should be at least one character before and after the dot
    return false;
  }

  const name = fileName.slice(0, lastDotIndex);
  const extension = fileName.slice(lastDotIndex + 1);

  // Validate the file name part
  const isNameValid = /^[a-zA-Z0-9_-]+$/.test(name);

  // Validate the file extension part
  const isExtensionValid = /^[a-zA-Z0-9]+$/.test(extension);

  // Return true only if both parts are valid
  return isNameValid && isExtensionValid;
};

// return the date in correct timezone
export const dateInCorrectTimezone = (selectedDate) => {
  return moment.tz(selectedDate, platformConfig.timezone).startOf("day").format("DD/MM/YYYY");
};

// return today date
export const checkTodayDate = () => {
  return moment().startOf("day").format("DD/MM/YYYY");
};

export const checkTodayDateWithFormat = (format) => {
  return moment().startOf("day").format(format);
};

export const standardFormatToday = () => {
  const momentObject = moment().startOf("day");

  const year = momentObject.year();
  const month = momentObject.month();
  const day = momentObject.date();
  const hours = momentObject.hours();
  const minutes = momentObject.minutes();
  const seconds = momentObject.seconds();

  const todayInSpecifiedTimezone = new Date(year, month, day, hours, minutes, seconds);

  return todayInSpecifiedTimezone;
};

export const findStoreIds = (storeNames, fetchDateBasedValues = false) => {
  const storeDetails = store.getState().userSelections;
  const reformStoreNames = Array.isArray(storeNames) ? storeNames : [storeNames];
  const storeListKeyword = fetchDateBasedValues === true ? "store_dates" : "stores_list";
  let storeIds =
    typeof storeDetails[storeListKeyword] !== "undefined" && storeDetails[storeListKeyword].length !== 0 && reformStoreNames.length !== 0
      ? reformStoreNames.map(name => {
        const foundOptionName = storeDetails[storeListKeyword].find(storeListOptions => storeListOptions.name === name);
        return typeof foundOptionName !== "undefined" ? foundOptionName.id : ""
      }).filter(id => id !== "")
      : [];
  return storeIds;
};

// fetch store list options for tableau filters
export const storeListOptionsForFilter = (usedFor, singleValue = false) => {
  const storeDetails = store.getState().userSelections;
  let storeList =
    typeof usedFor !== "undefined" && usedFor === "campaign"
      ? typeof storeDetails.campaign_detail !== "undefined" && storeDetails.campaign_detail.campaign_stores?.length > 0
        ? [...storeDetails.campaign_detail.campaign_stores].sort()
        : []
      : typeof storeDetails.store_dates !== "undefined" && storeDetails.store_dates.length !== 0
        ? [
          ...storeDetails.store_dates.map((storeInfo) => {
            return storeInfo.name;
          }),
        ].sort()
        : [];

  if (singleValue === true) {
    const removeAllOption = storeList.sort().filter((item) => item !== "(All)");
    return removeAllOption[0];
  } else {
    return storeList;
  }
};

export const independentStoreListOptions = (singleValue = false) => {
  const storeDetails = store.getState().userSelections;
  let storeList =
    typeof storeDetails.stores_list !== "undefined" && storeDetails.stores_list.length !== 0
      ? [
        ...storeDetails.stores_list
          // .filter((store) => {
          //   return store.active === true;
          // })
          .map((storeInfo) => {
            return storeInfo.name;
          }),
      ].sort()
      : [];

  if (singleValue === true) {
    const removeAllOption = storeList.sort().filter((item) => item !== "(All)");
    return removeAllOption[0];
  } else {
    return storeList;
  }
};

export const yesterdayDate = () => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  // set end date
  return dbFormatDate(yesterday);
};

/*
  Decides whether the brand report of a campaign may be shared/managed.
  It is allowed as soon as either report is ready:
    - the performance report (campaign status code 4), or
    - the impressions report (impression report status 1)
  Scheduled campaigns (not started yet) and completed campaigns (status code 5, no reporting needed) stay unshareable.

  The campaign object comes in two shapes:
    - campaign list records keep the progress status in "campaign_status"
    - the single campaign detail keeps it in "campaign_active_status"
  Both hold the same values returned by checkStatus() ("Scheduled" / "Active" / "Completed").
*/
export const isBrandReportShareable = (campaign) => {
  if (typeof campaign === "undefined" || campaign === null) {
    return false;
  }

  const progressStatus =
    typeof campaign.campaign_status !== "undefined" ? campaign.campaign_status : campaign.campaign_active_status;

  if (progressStatus === "Scheduled") {
    return false;
  }

  const performanceReportReady = campaign.campaign_status_code === 4;
  const impressionsReportReady = campaign.impression_report_status === 1 && campaign.campaign_status_code !== 5;

  return performanceReportReady || impressionsReportReady;
};

// reset brand report view
export const resetBrandReportView = () => {
  store.dispatch(
    updateStateData({
      brand_report_view: false,
    })
  );
};

export const clearCampaignDetails = () => {
  store.dispatch(
    deleteItems([
      "adlevel_names",
      "activities_data",
      "manual_activity_list",
      "activityOptions",
      "campaign_weeks",
      "stores_list",
      "brand_filters",
      "retailer_filters",
      "campaign_detail",
      "embedded_performance_filter",
      "embedded_impression_filter",
      "visual_names_info",
      "clip_names_info",
      "brand_filters_loading",
      // Analytics campaign stores (separate from Add Clip stores_list_for_visuals)
      "analytics_campaign_stores_list",
      "analytics_campaign_stores_scope_empty",
      "analytics_report_unavailable_reason",
    ])
  );
};

/** Clears campaign / clips / loops listing caches (not campaign detail or wizard state). */
export const resetListingCaches = () => {
  store.dispatch(
    deleteItems([
      "campaign_list",
      "clips_list",
      "clip_names_list",
      "clip_company_name_list",
      "clip_stores_list_filter",
      "clip_names_info",
      "filtered_clips_list",
      "loop_campaign_list",
      "loop_names_list",
      "loop_company_name_list",
      "loop_stores_filter_list",
      "loop_names_info",
      "visual_names_info",
      "filtered_loops_list",
      "campaign_names_info",
      "companies_info",
      "independent_owners_list",
      "available_to_list",
    ])
  );
};

export const erase_duplicates = (options) => {
  var obj = [{}];
  var arr = [];
  if(options === undefined){
    return []
  }
  options.forEach((option) => {
    if (!arr.includes(option.label)) {
      obj.push(option);
      arr.push(option.label);
    }
  });
  const filteredObj = obj.filter((item) => Object.keys(item).length > 0);
  return filteredObj;
};

// Duplicate names are collapsed to a single option in the filter dropdowns (see
// erase_duplicates), so a selected name only carries one of the ids that share it.
// Map the selected ids back to their names and collect every id with a matching name,
// so a query built from the result targets all records with that name, not just one.
export const collectIdsBySharedName = (selectedIds, nameList) => {
  if (!Array.isArray(selectedIds) || selectedIds.length === 0) return [];
  if (!Array.isArray(nameList) || nameList.length === 0) return selectedIds;

  const selectedLabels = new Set(
    nameList
      .filter((option) => selectedIds.includes(option.id))
      .map((option) => option.label)
  );

  const matchingIds = nameList
    .filter((option) => selectedLabels.has(option.label))
    .map((option) => option.id);

  // union with the original selection so an id absent from nameList is never dropped
  return [...new Set([...selectedIds, ...matchingIds])];
};

export const getFirstDayMonthsAgo = (monthCount) => {
  return moment().subtract(monthCount, "months").startOf("month").format("YYYY-MM-DD");
};

export const getFirstDayMonthsAfter = (monthCount) => {
  return moment().add(monthCount, "months").startOf("month").format("YYYY-MM-DD");
};

export const getFirstDayTwoMonthsAgo = () => {
  return getFirstDayMonthsAgo(2);
  // return moment().subtract(2, "months").startOf("month").format("YYYY-MM-DD");
};

export const getFirstDayEightMonthsAgo = () => {
  return getFirstDayMonthsAgo(7);
  // return moment().subtract(7, "months").startOf("month").format("YYYY-MM-DD");
};

export const verifyBrowserName = () => {
  const userAgent = navigator.userAgent;

  if (userAgent.includes("Chrome") && !userAgent.includes("Edg") && !userAgent.includes("OPR")) {
    return "Google Chrome";
  } else if (userAgent.includes("Safari") && !userAgent.includes("Chrome")) {
    return "Safari";
  } else if (userAgent.includes("Firefox")) {
    return "Mozilla Firefox";
  } else if (userAgent.includes("Edg")) {
    return "Microsoft Edge";
  } else if (userAgent.includes("OPR") || userAgent.includes("Opera")) {
    return "Opera";
  } else if (userAgent.includes("Trident") || userAgent.includes("MSIE")) {
    return "Internet Explorer";
  } else {
    return "Unknown Browser";
  }
};

// delete pagination cookies
export const deletePaginationCookies = (calledFrom) => {

  if (calledFrom === "campaign") {
    // delete stores/pictures/clips pagination cookies
    deleteCookie("stores_pagination", true);
    deleteCookie("clips_pagination", true);
    deleteCookie("loops_pagination", true);
    deleteCookie("loop_list_filters", true);
    deleteCookie("clip_list_filters", true);
    deleteCookie("store_list_filters", true);
    deleteCookie("users_pagination", true);
    deleteCookie("users_filter", true);
    deleteCookie("agency_users_filter", true);
    deleteCookie("tenant_users_filter", true);
    deleteCookie("cyreen_users_filter", true);
    deleteCookie("brand_users_filter", true);

    store.dispatch(deleteItems([
      "stores_pagination",
      "clips_pagination",
      "loops_pagination",
      "loop_list_filters",
      "clip_list_filters",
      "store_list_filters",
      "clips_list_filter_applied",
      "loop_list_filter_applied",
      "store_list_filter_applied",
      "users_pagination",
    ]));

  } else if (calledFrom === "clips") {
    // delete pictures/campaign/stores pagination cookies
    deleteCookie("stores_pagination", true);
    deleteCookie("campaign_pagination", true);
    deleteCookie("loops_pagination", true);
    deleteCookie("loop_list_filters", true);
    deleteCookie("campaign_list_filters", true);
    deleteCookie("store_list_filters", true);
    deleteCookie("users_pagination", true);
    deleteCookie("users_filter", true);
    deleteCookie("agency_users_filter", true);
    deleteCookie("tenant_users_filter", true);
    deleteCookie("cyreen_users_filter", true);
    deleteCookie("brand_users_filter", true);
    store.dispatch(deleteItems([
      "stores_pagination",
      "campaign_pagination",
      "loops_pagination",
      "loop_list_filters",
      "campaign_list_filters",
      "store_list_filters",
      "campaign_list_filter_applied",
      "loop_list_filter_applied",
      "store_list_filter_applied",
      "users_pagination",
    ]));
  } else if (calledFrom === "loops_campaign") {
    // delete stores/campaign/clips pagination cookies
    deleteCookie("stores_pagination", true);
    deleteCookie("campaign_pagination", true);
    deleteCookie("clips_pagination", true);
    deleteCookie("clip_list_filters", true);
    deleteCookie("campaign_list_filters", true);
    deleteCookie("store_list_filters", true);
    deleteCookie("users_pagination", true);
    deleteCookie("users_filter", true);
    deleteCookie("agency_users_filter", true);
    deleteCookie("tenant_users_filter", true);
    deleteCookie("cyreen_users_filter", true);
    deleteCookie("brand_users_filter", true);
    store.dispatch(deleteItems([
      "stores_pagination",
      "campaign_pagination",
      "clips_pagination",
      "clip_list_filters",
      "campaign_list_filters",
      "store_list_filters",
      "campaign_list_filter_applied",
      "clips_list_filter_applied",
      "store_list_filter_applied",
      "users_pagination",
    ]));
  }
  else if (calledFrom === "infrastructure") {
    // delete pictures/campaign/clips pagination cookies
    deleteCookie("loops_pagination", true);
    deleteCookie("campaign_pagination", true);
    deleteCookie("clips_pagination", true);
    deleteCookie("clip_list_filters", false);
    deleteCookie("campaign_list_filters", true);
    deleteCookie("loop_list_filters", true);
    deleteCookie("users_pagination", true);
    deleteCookie("users_filter", true);
    deleteCookie("agency_users_filter", true);
    deleteCookie("tenant_users_filter", true);
    deleteCookie("cyreen_users_filter", true);
    deleteCookie("brand_users_filter", true);
    store.dispatch(deleteItems([
      "loops_pagination",
      "campaign_pagination",
      "clips_pagination",
      "clip_list_filters",
      "campaign_list_filters",
      "loop_list_filters",
      "campaign_list_filter_applied",
      "clips_list_filter_applied",
      "loop_list_filter_applied",
      "users_pagination",
    ]));
  } else if (calledFrom === "users") {
    // delete pictures/campaign/stores pagination cookies
    deleteCookie("stores_pagination", true);
    deleteCookie("campaign_pagination", true);
    deleteCookie("loops_pagination", true);
    deleteCookie("clips_pagination", true);
    deleteCookie("clip_list_filters", true);
    deleteCookie("campaign_list_filters", true);
    deleteCookie("store_list_filters", true);
    deleteCookie("loop_list_filters", true);

    store.dispatch(deleteItems([
      "stores_pagination",
      "loops_pagination",
      "campaign_pagination",
      "clips_pagination",
      "clip_list_filters",
      "campaign_list_filters",
      "loop_list_filters",
      "store_list_filters",
      "campaign_list_filter_applied",
      "clips_list_filter_applied",
      "loop_list_filter_applied",
      "store_list_filter_applied",
    ]));
  } else {
    // delete all cookies
    deleteCookie("stores_pagination", true);
    deleteCookie("campaign_pagination", true);
    deleteCookie("loops_pagination", true);
    deleteCookie("clips_pagination", true);
    deleteCookie("clip_list_filters", true);
    deleteCookie("campaign_list_filters", true);
    deleteCookie("store_list_filters", true);
    deleteCookie("loop_list_filters", true);
    deleteCookie("users_pagination", true);
    deleteCookie("users_filter", true);
    deleteCookie("agency_users_filter", true);
    deleteCookie("tenant_users_filter", true);
    deleteCookie("cyreen_users_filter", true);
    deleteCookie("brand_users_filter", true);
    store.dispatch(deleteItems([
      "stores_pagination",
      "loops_pagination",
      "campaign_pagination",
      "clips_pagination",
      "clip_list_filters",
      "campaign_list_filters",
      "loop_list_filters",
      "store_list_filters",
      "campaign_list_filter_applied",
      "clips_list_filter_applied",
      "loop_list_filter_applied",
      "store_list_filter_applied",
      "users_pagination",
    ]));
  }
}

export const scrollTableListings = (reduxKeyword) => {
  const currentStateData = store.getState().userSelections;
  if (typeof currentStateData[reduxKeyword] !== "undefined" && typeof currentStateData[reduxKeyword].scroll_pos) {
    window.scrollTo(0, currentStateData[reduxKeyword].scroll_pos);
  }
}

// Landing overview versions. Customers stay on the company dashboard.
// Tenant and Cyreen follow the space they entered, not their company root.
const LANDING_OVERVIEW_DESPAR_ROOT_ID = 73;
const LANDING_OVERVIEW_RTG_ROOT_ID = 92;
const GERMAN_TENANT_SPACE_NAME = "cyreen gmbh";

const landingOverviewDashboards = () =>
  platformConfig.embeddable_dashboards.overview.landing_page_overview;

// Same cookie checkSelectedTenantFromCookies reads. Kept here so this file does not import loginFlow.
const selectedTenantAccessName = () => {
  const row = document.cookie.split("; ").find((cookie) => cookie.startsWith("selected_tenant="));
  if (typeof row === "undefined") {
    return "";
  }
  try {
    const selectedTenant = JSON.parse(row.substring("selected_tenant=".length));
    return typeof selectedTenant.access_name === "string" ? selectedTenant.access_name : "";
  } catch {
    return "";
  }
};

export const findLandingOverviewEmbed = () => {
  const dashboards = landingOverviewDashboards();
  const desparId = dashboards[LANDING_OVERVIEW_DESPAR_ROOT_ID];
  const rtgId = dashboards[LANDING_OVERVIEW_RTG_ROOT_ID];
  const masterId = dashboards.master;

  const userDetails = checkUserDetailsFromCookie();
  const userDetail = userDetails.success === true ? userDetails.user_detail : {};
  const userType = typeof userDetail.user_type === "string" ? userDetail.user_type.trim().toLowerCase() : "";

  if (userType === "tenant" || userType === "cyreen") {
    const spaceName = selectedTenantAccessName().trim().toLowerCase();
    return spaceName === GERMAN_TENANT_SPACE_NAME ? rtgId : desparId;
  }

  const rootId = Number(userDetail.root_company_id);
  if (rootId === LANDING_OVERVIEW_RTG_ROOT_ID) {
    return rtgId;
  }
  if (rootId === LANDING_OVERVIEW_DESPAR_ROOT_ID) {
    return desparId;
  }
  return masterId;
};

// Filters stay off only on the RTG version. Master and Despar keep them on.
export const landingOverviewFiltersEnabled = (embedId) => {
  return embedId !== landingOverviewDashboards()[LANDING_OVERVIEW_RTG_ROOT_ID];
};

export const findEmbeddableId = (lookForConfig, module_name) => {
  const checkUserDetails = checkUserDetailsFromCookie();
  const userDetailObj = checkUserDetails?.user_detail;
  const embeddableConfigData = platformConfig.embeddable_config[lookForConfig];
  if (typeof embeddableConfigData !== "undefined" && typeof embeddableConfigData[module_name] !== "undefined") {
    const useCustomized = embeddableConfigData[module_name].includes(userDetailObj.root_company_id);
    // if the embeddable config is customized for the user, then return the customized id
    if (useCustomized === true) {
      return platformConfig.embeddable_dashboards[lookForConfig][module_name][userDetailObj.root_company_id]
    } else {
      return platformConfig.embeddable_dashboards[lookForConfig][module_name].master;
    }
  } else {
    return "not_found";
  }
}

export const embeddableFilterEnabled = (lookForConfig, module_name) => {
  const checkUserDetails = checkUserDetailsFromCookie();
  const userDetailObj = checkUserDetails?.user_detail;
  const embeddableConfigData = platformConfig.embeddable_filter_config[lookForConfig];

  if (typeof embeddableConfigData !== "undefined" && typeof embeddableConfigData[module_name] !== "undefined") {
    const filterEnabled = typeof embeddableConfigData[module_name][userDetailObj.root_company_id] !== "undefined" ? embeddableConfigData[module_name][userDetailObj.root_company_id] : embeddableConfigData[module_name].master;
    // if the embeddable config is customized for the user, then return the customized id
    return filterEnabled
  } else {
    return true;
  }
}

export const getBackendVersion = async () => {
  try {
    const res = await sendApiRequest("version", {}, "GET");
    if (res !== undefined && res.success) {
      return res.response.version;
    }
    return "n/a";
  } catch (error) {
    return "n/a";
  }
}
export const getMonthFromHeader = (headerText) => {
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  for (let i = 0; i < monthNames.length; i++) {
    if (headerText.includes(monthNames[i])) {
      return i;
    }
  }
  return new Date().getMonth();
};

export const getWeekBounds = (weekNumber, year, displayedMonth) => {
  let targetYear = year;

  if (displayedMonth === 0 && weekNumber >= 52) {
    targetYear = year - 1;
  }
  if (displayedMonth === 11 && weekNumber === 1) {
    targetYear = year + 1;
  }

  const monday = moment().year(targetYear).isoWeek(weekNumber).startOf("isoWeek");
  const sunday = moment().year(targetYear).isoWeek(weekNumber).endOf("isoWeek");

  return {
    monday: new Date(monday.year(), monday.month(), monday.date(), 0, 0, 0),
    sunday: new Date(sunday.year(), sunday.month(), sunday.date(), 0, 0, 0),
  };
};

export const clampDate = (date, min, max) => {
  let result = new Date(date.getTime());
  if (min && result < min) {
    result = new Date(min.getTime());
  }
  if (max && result > max) {
    result = new Date(max.getTime());
  }
  return result;
};

// fieldRole "start"/"end" checks the day that role commits; otherwise any overlap.
export const isWeekSelectableInRange = (
  weekNumber,
  year,
  displayedMonth,
  globalMin,
  globalMax,
  fieldRole
) => {
  const { monday, sunday } = getWeekBounds(weekNumber, year, displayedMonth);

  if (!globalMin && !globalMax) return true;

  if (fieldRole === "end") {
    return (!globalMin || sunday >= globalMin) && (!globalMax || sunday <= globalMax);
  }
  if (fieldRole === "start") {
    return (!globalMin || monday >= globalMin) && (!globalMax || monday <= globalMax);
  }

  const afterMin = !globalMin || sunday >= globalMin;
  const beforeMax = !globalMax || monday <= globalMax;

  return afterMin && beforeMax;
};

export const showRequestCancelButton = (showHideFlag) => {
  store.dispatch(updateStateData({
    show_loading_cancel_button: showHideFlag,
  }));
}

// to return sort icon based on sorting direction
export const SortIcon = ({ direction }) => {
  return direction === "asc" ? <ArrowDropDown /> : <ArrowDropUp />;
};

// markup to return the detailed selection icon with tooltip
export const DetailedSelectionIconWithTooltip = (onClickFunc) => {
  return <HtmlTooltip title="Detailed Selection">
    <IconButton
      size="small"
      onClick={(e) => {
        onClickFunc(e);
      }}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onKeyDown={(e) => {
        e.stopPropagation();
      }}
      sx={{
        p: 0,
        display: "inline-flex",
        alignItems: "center",
        // Same inward focus ring as the date-range filter controls, so the
        // outline stays inside the icon and does not sit on the field underline.
        "&:focus, &.Mui-focusVisible": {
          outline: "2px solid #AF3241",
          outlineOffset: -2,
          backgroundColor: "transparent",
        },
      }}
    >
      <FilterAlt sx={{ color: "#AF3241" }} />
    </IconButton>
  </HtmlTooltip>
}
